// Eskom PARCC — Cloud Storage object-path helpers (pure, Firebase-free).
//
// Kept free of any `firebase/storage` import so the path construction and the
// upload-validation rules are unit-testable in isolation (see
// test/lib/storagePaths.test.ts) — exactly the pure/binary split used for the
// report + export download glue. The Firebase-touching upload/fetch layer lives
// in src/lib/storage.ts and composes these.
//
// Path conventions (mirrored by storage.rules):
//   avatars/{uid}/{file}   — a user's own profile photo (owner-write, signed-in read)
//   reports/{uid}/{file}   — a generated compliance report (admin-write/read)

/** Defensive per-file size caps (bytes). Bounds, not business rules. */
export const AVATAR_MAX_BYTES = 5 * 1024 * 1024; // 5 MB
export const REPORT_MAX_BYTES = 50 * 1024 * 1024; // 50 MB

/** The image MIME types we accept for an avatar upload. */
const AVATAR_MIME = /^image\/(png|jpe?g|gif|webp|svg\+xml)$/i;

/**
 * Sanitise one path segment: strip directory separators and unsafe characters,
 * collapse runs to a single dash, and cap the length. Never returns an empty
 * string (falls back to `file`) so a path is always well-formed. This is a
 * defence-in-depth complement to the Storage rules — the rules are the real
 * boundary; this keeps keys tidy and traversal-free.
 */
export function sanitizeStorageSegment(name: string): string {
  const base = (name ?? '').toString().trim();
  const cleaned = base
    .replace(/[/\\]+/g, '-') // no path separators (no traversal into other prefixes)
    .replace(/[^A-Za-z0-9._-]+/g, '-') // control chars + everything unsafe -> dash
    .replace(/-{2,}/g, '-') // collapse runs
    .replace(/^[-.]+|[-.]+$/g, '') // no leading/trailing dot or dash
    .slice(0, 120);
  return cleaned || 'file';
}

/** Build the object path for a user's avatar: `avatars/{uid}/{file}`. */
export function avatarObjectPath(uid: string, fileName: string): string {
  return `avatars/${sanitizeStorageSegment(uid)}/${sanitizeStorageSegment(fileName)}`;
}

/** Build the object path for a generated report: `reports/{uid}/{file}`. */
export function reportObjectPath(uid: string, fileName: string): string {
  return `reports/${sanitizeStorageSegment(uid)}/${sanitizeStorageSegment(fileName)}`;
}

/**
 * Distinguish a Cloud Storage object path from a legacy absolute URL. Profiles
 * captured before the upload feature hold an `http(s)://` (or `data:`/`gs:`) URL
 * in `photoUrl`; new uploads store a bare object path. Renderers use this to
 * decide whether to fetch via the Storage SDK or use the value as-is.
 */
export function isStoragePath(value: string | null | undefined): boolean {
  if (!value) return false;
  const v = value.trim();
  if (v === '') return false;
  return !/^[a-z][a-z0-9+.-]*:\/\//i.test(v) && !/^(data|gs|blob):/i.test(v);
}

/** Minimal shape of the parts of a File we validate (keeps this Firebase/DOM-free). */
export interface UploadableFile {
  readonly type: string;
  readonly size: number;
  readonly name: string;
}

/**
 * Validate a candidate avatar upload. Returns a human-readable error message, or
 * `null` if the file is acceptable (image MIME + within the size cap). The same
 * constraints are enforced server-side by storage.rules — this is the friendly,
 * pre-flight client check.
 */
export function validateAvatarFile(
  file: UploadableFile,
  maxBytes: number = AVATAR_MAX_BYTES,
): string | null {
  if (!file || file.size === 0) return 'That file looks empty.';
  if (!AVATAR_MIME.test(file.type)) {
    return 'Choose an image file (PNG, JPEG, GIF, WebP or SVG).';
  }
  if (file.size > maxBytes) {
    const mb = Math.round((maxBytes / (1024 * 1024)) * 10) / 10;
    return `Image is too large — keep it under ${mb} MB.`;
  }
  return null;
}
